import React from 'react';
import { LogIn, Sparkles } from 'lucide-react';
import { useViewport } from '../../contexts/ViewportContext';
import { useStatusBarInfo } from '../../hooks/useStatusBarInfo';

interface HeroProps {
  onAuthClick: (mode: 'login' | 'signup') => void;
}

export function Hero({ onAuthClick }: HeroProps) {
  const { isMobile } = useViewport();
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();

  return (
    <div
      className="relative min-h-screen flex flex-col justify-center px-4 sm:px-6 lg:px-8 overflow-hidden py-16"
      style={{
        paddingTop: isMobile && isNativeApp ? `calc(4rem + ${statusBarHeight}px)` : '4rem',
      }}
    >
      {/* Content */}
      <div className="relative max-w-6xl mx-auto text-center flex-1 flex flex-col justify-center">
        <div className="flex flex-wrap justify-center gap-3 mb-10 animate-fade-in">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 hover:bg-purple-500/20 transition-colors"
            style={{ backdropFilter: 'blur(8px)' }}
          >
            <Sparkles className="w-4 h-4 animate-pulse" />
            <span className="text-sm font-medium">OMG! They cracked the code.</span>
          </div>
        </div>

        <h1 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white font-['Sora'] leading-tight lg:leading-tight animate-fade-in">
          Welcome to{' '}
          <span className="relative inline-block pt-4">
            <span className="text-3xl sm:text-5xl lg:text-7xl relative z-10 bg-gradient-to-r from-blue-400 via-blue-500 to-blue-400 text-transparent bg-clip-text bg-[length:200%_100%] animate-gradient">
              Project Management Heaven
            </span>
            <span className="absolute inset-x-0 bottom-0 h-3 bg-gradient-to-r from-blue-400/0 via-blue-500/40 to-blue-400/0 blur-lg" />
          </span>
        </h1>

        <p className="mt-10 text-xl lg:text-2xl/15 text-blue-200 max-w-3xl mx-auto leading-relaxed animate-fade-in-up">
          ZEN is your AI-Native superpower. <br /> So fluid you'll wonder how you ever got anything
          done without it.
        </p>

        <div
          className="mt-16 flex flex-wrap justify-center gap-6 animate-fade-in-up"
          style={{ animationDelay: '200ms' }}
        >
          <button
            onClick={() => onAuthClick('signup')}
            className="group relative bg-gradient-to-r from-blue-500 via-blue-400 to-blue-500 text-white px-8 py-4 rounded-lg text-lg font-medium flex items-center gap-3 shadow-[0_0_20px_rgba(59,91,219,0.3)] hover:shadow-[0_0_25px_rgba(59,91,219,0.5)] transition-all duration-500 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500" />
            <Sparkles size={24} className="relative z-10 animate-pulse" />
            <span className="relative z-10">Get Started Free</span>
          </button>

          <button
            onClick={() => onAuthClick('login')}
            className="group relative bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 text-white px-8 py-4 rounded-lg text-lg font-medium flex items-center gap-3 shadow-btn hover:shadow-btn-hover transition-all duration-500 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500" />
            <LogIn size={24} className="relative z-10" />
            <span className="relative z-10">Sign In</span>
          </button>
        </div>

        {/* Stats - Commented out to give more breathing room
        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-4xl mx-auto">
          {[
            { label: 'Active Users', value: '10,000+' },
            { label: 'Projects Created', value: '50,000+' },
            { label: 'AI Conversations', value: '500K+' },
            { label: 'Time Saved', value: '100K+ hrs' },
          ].map((stat, index) => (
            <div
              key={stat.label}
              className="group text-center animate-fade-in-up bg-gray-800/30 backdrop-blur-sm rounded-xl p-4 border border-gray-700/50 hover:border-blue-500/50 transition-colors duration-300"
              style={{ animationDelay: `${300 + index * 100}ms` }}
            >
              <div className="text-3xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                {stat.value}
              </div>
              <div className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
        */}
      </div>
    </div>
  );
}
